// Apollo
import { apolloClient } from '@/graphql';
import gql from 'graphql-tag';

// Update
import UpdateMatch from '@/graphql/match/update/updateMatch.gql';
import UpdateMatches from '@/graphql/match/update/updateMatchV2.gql';
import MatchHomeScore from '@/graphql/match/update/homeScore.gql';
import MatchAwayScore from '@/graphql/match/update/awayScore.gql';
import MatchReferee from '@/graphql/match/update/referee.gql';
import ManOfTheMatch from '@/graphql/match/update/manOfTheMatch.gql';
import MatchDate from '@/graphql/match/update/date.gql';
import MatchPitch from '@/graphql/match/update/pitch.gql';
import MatchKickOffTime from '@/graphql/match/update/kickOffTime.gql';
import MatchHomeTeam from '@/graphql/match/update/homeTeam.gql';
import MatchAwayTeam from '@/graphql/match/update/awayTeam.gql';
import MatchLength from '@/graphql/match/update/matchLength.gql';
import MatchTimeSlot from '@/graphql/match/update/matchTimeSlot.gql';
import MatchPublish from '@/graphql/match/update/publish.gql';
import MatchPostpone from '@/graphql/match/update/postpone.gql';
import MatchBothTeams from '@/graphql/match/update/bothTeams.gql';
import GetMatchesByLocation from '@/graphql/match/query/GetMatchesByLocation.gql';

// Delete
import DeleteMatch from '@/graphql/match/delete/deleteMatch.gql';

export default {
	state: {
		matches: [] as Match[],
	},

	// ================ Getters ================
	getters: {
		matches(state: any) {
			return state.matches;
		},
	},

	// ================ Mutations ================
	mutations: {},

	// ================= Actions =================
	actions: {
		async createMatches({ commit, dispatch }: any, { leagueId, matches }: any) {
			const formattedMatches = matches.map((match: any) => {
				const m = {
					date: match.date,
					game_length: match.gameLength,
					kick_off_time: match.kickOff,
					pitch: match.pitch.id,
					referee: match.referee,
					location: match.location,
					slot_time: parseInt(match.slot),
					league: leagueId,
				} as any;
				if (match.homeTeam.id) m.home_team = match.homeTeam.id || match.homeTeam;
				if (match.awayTeam.id) m.away_team = match.awayTeam.id || match.awayTeam;
				if (match.placeholder_home_team) m.placeholder_home_team = match.placeholder_home_team.id || match.placeholder_home_team;
				if (match.placeholder_away_team) m.placeholder_away_team = match.placeholder_away_team.id || match.placeholder_away_team;
				return m;
			});

			const response = await apolloClient
				.mutate({
					mutation: gql`
						mutation insert_multiple_matches($objects: [matches_insert_input!]!) {
							insert_matches(objects: $objects) {
								returning {
									id
								}
							}
						}
					`,
					// Parameters
					variables: {
						objects: [...formattedMatches],
					},
				})
				.catch(err => {
					console.debug(err);
				});

			const league = await dispatch('getLeagueById', { leagueId });
			// commit('setSelectedLeague', league);

			return (response as any).data.insert_matches.returning;
		},

		async createLeagueMatches({ commit, dispatch }: any, { leagueId, matches }: any) {
			const response = await apolloClient
				.mutate({
					mutation: gql`
						mutation insert_multiple_matches($objects: [matches_insert_input!]!) {
							insert_matches(objects: $objects) {
								returning {
									id
								}
							}
						}
					`,
					// Parameters
					variables: {
						objects: [...matches],
					},
				})
				.catch(err => {
					console.debug(err);
				});

			const league = await dispatch('getLeagueById', { leagueId });
			// commit('setSelectedLeague', league);

			return (response as any).data.insert_matches.returning;
		},

		async insertMatches({ commit, dispatch }: any, matches: any) {
			const response = await apolloClient
				.mutate({
					mutation: gql`
						mutation insert_multiple_matches($objects: [matches_insert_input!]!) {
							insert_matches(objects: $objects) {
								returning {
									id
								}
							}
						}
					`,
					variables: {
						objects: [...matches],
					},
				})
				.catch(err => {
					console.debug(err);
				});

			return (response as any).data.insert_matches.returning;
		},

		async createTournamentMatches({ commit, dispatch }: any, { matches, round }: any) {
			const response = await apolloClient
				.mutate({
					mutation: gql`
						mutation insert_multiple_matches($objects: [matches_insert_input!]!) {
							insert_matches(objects: $objects) {
								returning {
									id
								}
							}
						}
					`,
					// Parameters
					variables: {
						objects: [...matches],
					},
				})
				.catch(err => {
					console.debug(err);
				});

			// await dispatch('getLeagueById', { leagueId });

			return (response as any).data.insert_matches.returning;
		},

		async addMatchComment({ commit, dispatch }: any, { user, match, comment, leagueId, isPrivate }: any) {
			// Format comment
			const _comment = [
				{
					author: user.id,
					match,
					text: comment,
					private: isPrivate,
				},
			];

			const response = await apolloClient
				.mutate({
					mutation: gql`
						mutation insert_multiple_match_comments($objects: [match_comments_insert_input!]!) {
							insert_match_comments(objects: $objects) {
								returning {
									id
									match
									text
									created_at
									author: user {
										id
										first_name
										last_name
									}
								}
							}
						}
					`,
					// Parameters
					variables: {
						objects: [..._comment],
					},
				})
				.catch(err => {
					console.debug(err);
				});

			return (response as any).data.insert_match_comments.returning;
		},

		async deleteMatch({ commit, dispatch }: any, matchId: string) {
			const response = await apolloClient
				.mutate({
					mutation: gql`
						mutation delete_match($matchId: uuid!) {
							delete_matches(where: { id: { _eq: $matchId } }) {
								affected_rows
							}
						}
					`,
					// Parameters
					variables: {
						matchId,
					},
				})
				.catch(err => {
					console.debug(err);
				});

			return (response as any).data.delete_match;
		},

		async deleteMatchComment({ commit, dispatch }: any, { commentId, leagueId }: any) {
			const response = await apolloClient
				.mutate({
					mutation: gql`
						mutation delete_match_comments($commentId: uuid!) {
							delete_match_comments(where: { id: { _eq: $commentId } }) {
								returning {
									id
								}
							}
						}
					`,
					// Parameters
					variables: {
						commentId,
					},
				})
				.catch(err => {
					console.debug(err);
				});

			return (response as any).data.delete_match_comments.returning;
		},

		async updateMatch({ commit, dispatch }: any, { matchId, data, key }: any) {
			let mutation: any;
			let variables: any = {
				matchId,
				data,
			};

			switch (key) {
				case 'home_score':
					mutation = MatchHomeScore;
					break;
				case 'away_score':
					mutation = MatchAwayScore;
					break;
				case 'referee':
					mutation = MatchReferee;
					break;
				case 'man_of_the_match':
					mutation = MatchReferee;
					break;
				case 'date':
					mutation = MatchDate;
					break;
				case 'pitch':
					mutation = MatchPitch;
					break;
				case 'kick_off_time':
					mutation = MatchKickOffTime;
					break;
				case 'home_team':
					mutation = MatchHomeTeam;
					break;
				case 'away_team':
					mutation = MatchAwayTeam;
					break;
				case 'game_length':
					mutation = MatchLength;
					break;
				case 'time_slot':
					mutation = MatchTimeSlot;
					break;
				case 'published':
					mutation = MatchPublish;
					break;
				case 'postponed':
					mutation = MatchPostpone;
					break;
				case 'both-teams':
					mutation = MatchBothTeams;
					variables = {
						matchId,
						homeTeam: data[0],
						awayTeam: data[1],
					};
					break;
			}

			const response = await apolloClient
				.mutate({
					mutation,
					variables,
				})
				.catch(err => {
					console.debug(err);
				});

			const match = (response as any).data.update_matches.returning[0];

			commit('updateMatch', { matchId, data: match });
			return match;
		},

		async updateLeagueMatch({ commit, dispatch }: any, m: HasuraMatch) {
			const variables = {
				id: m.id,
				away_score: m.away_score,
				away_team: m.away_team,
				date: m.date,
				game_length: m.game_length,
				home_score: m.home_score,
				home_team: m.home_team,
				kick_off_time: m.kick_off_time,
				man_of_the_match: m.man_of_the_match,
				placeholder_away_team: m.placeholder_away_team,
				placeholder_home_team: m.placeholder_home_team,
				postponed: m.postponed,
				published: m.published,
				slot_time: m.slot_time,
				pitch: m.pitch,
				referee: m.referee,
			};

			const response = await apolloClient
				.mutate({
					mutation: UpdateMatch,
					variables,
				})
				.catch(err => {
					console.debug(err);
				});

			const match = (response as any).data.update_matches.returning[0];

			// commit('updateMatch', { matchId, data: match });
			return match;
		},

		async updateMatches({ commit, dispatch }: any, matches: any[]) {
			console.debug('updateMatches: updating matches...');
			const promises = matches.map((m: any) => {
				const object: HasuraMatch = {
					away_score: m.away_score,
					away_team: m.away_team,
					date: m.date,
					finals_group: m.finals_group || null,
					finals_knockout: m.finals_knockout || null,
					game_length: m.game_length,
					half_length: m.half_length,
					home_score: m.home_score,
					home_team: m.home_team,
					id: m.id,
					is_friendly: m.is_friendly,
					kick_off_time: m.kick_off_time,
					league: m.league,
					location: m.location,
					man_of_the_match: m.man_of_the_match,
					name: m.name,
					number_of_halves: m.number_of_halves,
					pitch: m.pitch,
					placeholder_away_team: m.placeholder_away_team,
					placeholder_home_team: m.placeholder_home_team,
					postponed: m.postponed,
					published: m.published,
					referee: m.referee,
					round: m.round,
					slot_time: m.slot_time,
					tournament: m.tournament,
				};

				return apolloClient
					.mutate({
						mutation: UpdateMatches,
						// Parameters
						variables: {
							id: m.id,
							object,
						},
					})
					.catch(err => {
						console.debug(err);
					});
			});

			const responses = await Promise.all([...promises]);

			const response = responses.map((res: any) => {
				return res.data.update_matches.returning[0];
			});

			return response;
		},

		async updateMatchesV2({ commit, dispatch }: any, matches: any[]) {
			console.debug('updateMatchesV2: updating matches...');

			const promises = matches.map((match: any) => {
				return apolloClient
					.mutate({
						mutation: UpdateMatches,
						// Parameters
						variables: {
							id: match.id,
							object: match,
						},
					})
					.catch(err => {
						console.debug(err);
					});
			});

			try {
				const responses = await Promise.all([...promises]);
				const response = responses.map((res: any) => {
					return res.data.update_matches.returning[0];
				});

				return response;
			} catch (error) {
				return error;
			}
		},

		async updateTournamentMatch({ dispatch }: any, match: HasuraMatch) {
			const res = await apolloClient.mutate({
				mutation: gql`
					mutation UpdateMatches(
						$id: uuid!
						$away_score: Int
						$away_team: uuid
						$date: date
						$game_length: String
						$home_score: Int
						$home_team: uuid
						$is_friendly: Boolean
						$kick_off_time: timetz
						$league: uuid
						$location: uuid
						$man_of_the_match: String
						$name: String
						$pitch: uuid
						$placeholder_away_team: uuid
						$placeholder_home_team: uuid
						$postponed: Boolean
						$published: Boolean
						$referee: uuid
						$round: String
						$slot_time: Int
						$tournament: uuid
					) {
						update_matches(
							where: { id: { _eq: $id } }
							_set: {
								away_score: $away_score
								away_team: $away_team
								date: $date
								game_length: $game_length
								home_score: $home_score
								home_team: $home_team
								is_friendly: $is_friendly
								kick_off_time: $kick_off_time
								league: $league
								man_of_the_match: $man_of_the_match
								name: $name
								pitch: $pitch
								placeholder_away_team: $placeholder_away_team
								placeholder_home_team: $placeholder_home_team
								postponed: $postponed
								published: $published
								referee: $referee
								round: $round
								slot_time: $slot_time
								tournament: $tournament
							}
						) {
							returning {
								id
							}
						}
					}
				`,
				// Parameters
				variables: {
					id: match.id,
					away_score: match.away_score,
					away_team: match.away_team,
					date: match.date,
					game_length: match.game_length,
					home_score: match.home_score,
					home_team: match.home_team,
					is_friendly: match.is_friendly,
					kick_off_time: match.kick_off_time,
					league: match.league,
					man_of_the_match: match.man_of_the_match,
					name: match.name,
					pitch: match.pitch,
					placeholder_away_team: match.placeholder_away_team,
					placeholder_home_team: match.placeholder_home_team,
					postponed: match.postponed,
					published: match.published,
					referee: match.referee,
					round: match.round,
					slot_time: match.slot_time,
					tournament: match.tournament,
				},
			});

			return res.data.update_matches_returning[0];
		},

		async deleteRound({ commit }: any, matchIds: any) {
			const promises = matchIds.map((matchId: string) => {
				return apolloClient
					.mutate({
						mutation: DeleteMatch,
						// Parameters
						variables: {
							matchId,
						},
					})
					.catch(err => {
						console.debug(err);
					});
			});

			await Promise.all([...promises]);

			return;
		},

		async getFriendlies({ commit }: any) {
			const response = await apolloClient.query({
				query: gql`
					query Friendlies {
						matches(where: { is_friendly: { _eq: true } }) {
							id
							away_score
							away_team
							created_at
							date
							game_length
							half_length
							home_score
							home_team
							is_friendly
							kick_off_time
							location
							man_of_the_match
							number_of_halves
							pitch
							placeholder_away_team
							placeholder_home_team
							player_type
							published
							referee
							slot_time
							team_size
							updated_at
							homeTeamObj: teamByHomeTeam {
								id
								name
							}
							awayTeamObj: team {
								id
								name
							}
							locationObj: locationByLocation {
								id
								name
								city {
									id
									name
									country_id
								}
							}
							matchComments: match_comments {
								id
								author
								text
							}
						}
					}
				`,
				fetchPolicy: 'no-cache',
			});

			const { matches } = response.data;

			return matches;
		},

		async getMatchById({ commit }: any, id: string) {
			const response = await apolloClient.query({
				query: gql`
					query GetMatchById($id: uuid!) {
						matches(where: { id: { _eq: $id } }) {
							away_score
							away_team
							created_at
							date
							finals_group
							finals_knockout
							game_length
							half_length
							home_score
							home_team
							id
							is_friendly
							kick_off_time
							league
							location
							man_of_the_match
							name
							number_of_halves
							pitch
							placeholder_away_team
							placeholder_home_team
							player_type
							postponed
							published
							referee
							round
							slot_time
							team_size
							tournament
							updated_at
							homeTeamObj: teamByHomeTeam {
								id
								name
								logo_url
							}
							awayTeamObj: team {
								id
								name
								logo_url
							}
							refereeObj: refereeByReferee {
								id
								first_name
								last_name
							}
							pitchObj: pitchByPitch {
								id
								name
								pitch_name
								location {
									id
									name
									city {
										id
										name
									}
								}
							}
							matchComments: match_comments {
								id
								author
								created_at
								private
								text
							}
							leagueObj: leagueByLeague {
								id
							}
						}
					}
				`,
				variables: {
					id,
				},
				fetchPolicy: 'no-cache',
			});

			const { matches } = response.data;

			return matches;
		},

		async getMatchesByLocation({ commit }: any, locationId: string) {
			try {
				const response = await apolloClient.query({
					query: GetMatchesByLocation,
					variables: {
						id: locationId,
					},
					fetchPolicy: 'network-only',
				});
				return response.data && response.data.matchesByLocation;
			} catch (error) {
				return [];
			}
		},

		// async getMatchesByLocation({ commit }: any, id: string) {
		// 	const response = await apolloClient.query({
		// 		query: gql`
		// 			query getMatchesByLocation($id: uuid!, $date: date) {
		// 				matches(where: { location: { _eq: $id }, date: { _gte: $date } }) {
		// 					id
		// 					home_team
		// 					home_score
		// 					half_length
		// 					finals_group
		// 					date
		// 					away_team
		// 					away_score
		// 					is_friendly
		// 					is_bye
		// 					kick_off_time
		// 					league
		// 					location
		// 					man_of_the_match
		// 					name
		// 					number_of_halves
		// 					pitch
		// 					placeholder_away_team
		// 					placeholder_home_team
		// 					player_type
		// 					postponed
		// 					published
		// 					referee
		// 					round
		// 					slot_time
		// 					team_size
		// 					leagueObj: leagueByLeague {
		// 						id
		// 						display_name
		// 					}
		// 					homeTeam: teamByHomeTeam {
		// 						id
		// 						name
		// 					}
		// 					phHome: placeholderTeamByPlaceholderHomeTeam {
		// 						id
		// 						name
		// 					}
		// 					phAway: placeholder_team {
		// 						id
		// 						name
		// 					}
		// 					awayTeam: team {
		// 						id
		// 						name
		// 					}
		// 					refereeObj: refereeByReferee {
		// 						id
		// 						first_name
		// 						last_name
		// 					}
		// 				}
		// 			}
		// 		`,
		// 		variables: {
		// 			id,
		// 		},
		// 		fetchPolicy: 'no-cache',
		// 	});

		// 	const { matches } = response.data;

		// 	return matches;
		// },
	},
};
